import { ApolloError } from '@apollo/client'
import { SUSPENSE } from '@rx-state/core'
import { combineLatest, filter, from, map, Observable, ObservedValueOf, take } from 'rxjs'
import { catchError, switchMap } from 'rxjs/operators'

import { GetCountriesDocument, GetCountriesQuery } from '../../../graphql/magento'
import { CustomError, InvalidResponseError } from '../../../utils/errorTools'
import { coerceCaughtToLeft } from '../../../utils/rx/errors'
import { mapRight } from '../../../utils/rx/operators'
import { SelectableTokenBaseCard, selectableTokenBaseCards$ } from '../../cybersource/cards'
import { latestApolloClient$ } from '../../graphql/apollo'
import { isValidCardType } from '../payment'
import { ValidFormValues } from '../types'

/**
 * Indicates that some query (e.g. selectable token base cards or countries) required for resolving
 * saved credit card data (from card id) has failed. This should not be used to indicate a network
 * error.
 */
export class SavedCreditCardDependencyError extends CustomError {
  override name = 'SavedCreditCardDependencyError'
}

/**
 * Saved credit card with the given id was not found.
 */
export class SavedCreditCardNotFoundError extends CustomError {
  override name = 'SavedCreditCardNotFoundError'
}

/**
 * Saved credit card is missing required fields or is otherwise invalid.
 */
export class SavedCreditCardInvalidError extends CustomError {
  override name = 'SavedCreditCardInvalidError'
}

export type SelectedSavedCreditCardResult =
  | {
      _tag: 'Left'
      error:
        | SavedCreditCardInvalidError
        | SavedCreditCardNotFoundError
        | SavedCreditCardDependencyError
        | ApolloError
        | Error
    }
  | {
      _tag: 'Right'
      data: Omit<Partial<ValidFormValues.CreditCard>, 'cardCVV' | 'code'>
    }

type GetCountriesQueryCountryData = NonNullable<
  NonNullable<GetCountriesQuery['countries']>[number]
> & { two_letter_abbreviation: string }
type GetCountriesQueryRegionData = NonNullable<
  NonNullable<GetCountriesQueryCountryData['available_regions']>[number]
>

const isNotSuspense = (
  result: ObservedValueOf<typeof selectableTokenBaseCards$>,
): result is Exclude<ObservedValueOf<typeof selectableTokenBaseCards$>, SUSPENSE> =>
  result !== SUSPENSE

/**
 * Try to resolve country data for saved credit card using `country_code` or `country_id`
 * (whichever is available). `country_id` is deprecated in favor of `country_code`, but
 * `country_code` does not seem to be populated yet.
 */
const resolveCountryDataByCodeOrId =
  ({
    country_code: countryCode,
    country_id: countryId,
  }: Pick<SelectableTokenBaseCard['address'], 'country_code' | 'country_id'>) =>
  (
    countries: NonNullable<GetCountriesQuery['countries']>,
  ):
    | { _tag: 'Left'; error: SavedCreditCardInvalidError }
    | { _tag: 'Right'; data: GetCountriesQueryCountryData } => {
    if (countryCode) {
      const resolvedByCode = countries.find(
        (country): country is GetCountriesQueryCountryData & { two_letter_abbreviation: string } =>
          !!country &&
          !!country.two_letter_abbreviation &&
          country.two_letter_abbreviation === countryCode,
      )
      return resolvedByCode
        ? { _tag: 'Right' as const, data: resolvedByCode }
        : {
            _tag: 'Left' as const,
            error: new SavedCreditCardInvalidError(`Country (code: ${countryCode}) not found `),
          }
    }
    if (!countryId) {
      return {
        _tag: 'Left' as const,
        error: new SavedCreditCardInvalidError('Missing country code (or id) in address'),
      }
    }
    const resolvedById = countries.find(
      (country): country is GetCountriesQueryCountryData & { two_letter_abbreviation: string } =>
        !!country && !!country.two_letter_abbreviation && country.id === countryId,
    )
    return resolvedById
      ? { _tag: 'Right' as const, data: resolvedById }
      : {
          _tag: 'Left' as const,
          error: new SavedCreditCardInvalidError(`Country (id: ${countryId}) not found`),
        }
  }

const resolveRegionData =
  (address: Pick<SelectableTokenBaseCard['address'], 'region'>) =>
  (
    countryData: GetCountriesQueryCountryData,
  ):
    | { _tag: 'Left'; error: SavedCreditCardInvalidError }
    | { _tag: 'Right'; data: GetCountriesQueryRegionData & { code: string } } => {
    const regionCode = address.region?.region_code
    if (!regionCode) {
      return {
        _tag: 'Left' as const,
        error: new SavedCreditCardInvalidError('Missing region code in address'),
      }
    }
    // Region code in saved credit card should be the region code, but may be the full name. Try to
    // resolve if possible.
    // - https://bitbucket.org/acadaca/dfh-magento2/src/5a940aa988dd8295e19e2518abfbc3648887982d/webroot/app/code/Magedelight/Cybersource/Model/Api/Soap.php?at=master#lines-116
    const resolvedByCode =
      countryData.available_regions?.find(
        (region): region is GetCountriesQueryRegionData & { code: string } =>
          !!region && !!region.code && region.code === regionCode,
      ) ??
      countryData?.available_regions?.find(
        (region): region is GetCountriesQueryRegionData & { code: string } =>
          !!region && !!region.code && region.name === regionCode,
      )
    return resolvedByCode
      ? { _tag: 'Right' as const, data: resolvedByCode }
      : {
          _tag: 'Left' as const,
          error: new SavedCreditCardInvalidError(`Region (code: ${regionCode}) not found`),
        }
  }

export const selectedSavedCreditCardValues$ = (
  savedCardId: string,
): Observable<SelectedSavedCreditCardResult> =>
  combineLatest([
    selectableTokenBaseCards$.pipe(
      filter(isNotSuspense),
      take(1),
      map((result) =>
        result.data
          ? { _tag: 'Right' as const, data: result.data }
          : {
              _tag: 'Left' as const,
              error: result.error
                ? result.error
                : result.errors?.length
                  ? new SavedCreditCardDependencyError(
                      `Selectable token base cards query failed: ${result.errors[0].message}`,
                    )
                  : new SavedCreditCardDependencyError(
                      'Selectable token base cards query data not returned',
                    ),
            },
      ),
      mapRight(({ data }) => {
        const selectedCard = data.find(({ hash }) => hash === savedCardId)
        return selectedCard
          ? { _tag: 'Right' as const, data: selectedCard }
          : {
              _tag: 'Left' as const,
              error: new SavedCreditCardNotFoundError(`Card (id: ${savedCardId}) not found`),
            }
      }),
    ),
    latestApolloClient$.pipe(
      take(1),
      switchMap((client) =>
        from(
          client.query({
            query: GetCountriesDocument,
            errorPolicy: 'none',
          }),
        ).pipe(
          map(({ data }) =>
            data?.countries
              ? { _tag: 'Right' as const, data: data.countries }
              : {
                  _tag: 'Left' as const,
                  error: new InvalidResponseError('Countries data not returned'),
                },
          ),
          catchError(coerceCaughtToLeft),
        ),
      ),
    ),
  ]).pipe(
    map(([cardResult, countriesResult]) =>
      // sequence results
      cardResult._tag === 'Left'
        ? cardResult
        : countriesResult._tag === 'Left'
          ? countriesResult
          : {
              _tag: 'Right' as const,
              data: { selectedCard: cardResult.data, countries: countriesResult.data },
            },
    ),
    mapRight(({ data: { selectedCard, countries } }) => {
      const {
        additional: { cc_exp_year, cc_type },
      } = selectedCard

      const countryDataResult = resolveCountryDataByCodeOrId(selectedCard.address)(countries)
      if (countryDataResult._tag === 'Left') {
        return countryDataResult
      }
      const regionDataResult = resolveRegionData(selectedCard.address)(countryDataResult.data)
      if (regionDataResult._tag === 'Left') {
        return regionDataResult
      }

      const regionId = regionDataResult.data.id

      return {
        _tag: 'Right' as const,
        data: {
          // It is false no matter what, since we are not even showing the option and the address comes
          // from the selectedCard not the forms.
          billingSameAsShipping: false,
          cardExpMonth: selectedCard.additional?.cc_exp_month || undefined,
          cardExpYear: cc_exp_year != null ? String(cc_exp_year) : undefined,
          cardNumber: selectedCard.additional?.cc_last4 || undefined,
          cardType: cc_type && isValidCardType(cc_type) ? cc_type : undefined,
          city: selectedCard.address?.city || undefined,
          country_code: countryDataResult.data.two_letter_abbreviation,
          firstname: selectedCard.address?.firstname || undefined,
          lastname: selectedCard.address?.lastname || undefined,
          postcode: selectedCard.address?.postcode || undefined,
          region: regionDataResult.data.code,
          ...(regionId && { region_id: String(regionId) }),
          saveCard: true,
          street1: selectedCard.address?.street?.[0] || undefined,
          street2: '',
          subscriptionId: selectedCard.hash || undefined,
          telephone: selectedCard.address?.telephone || undefined,
        },
      }
    }),
  )
